/**
 * lensPreference (HOF-106) — a page's last selected lens, remembered per tenant
 * in `localStorage`.
 *
 * This is HOF-101's `preisspiegel/katPreference.ts` with the page-specific half
 * removed. That module was written for one page; three more pages now want the
 * same mechanics, and the part they share — the key template and the two
 * try/catch blocks — is exactly the part that must not be retyped four times.
 * What stays with each page is what is genuinely its own: the key prefix, and
 * its notion of a *valid* selection (see each page's own `resolveStored…`).
 *
 * **Why `localStorage` and not a cookie.** The preference never needs to reach
 * the server, and a cookie would ride along on every request for no benefit. It
 * survives redeploys and stays local to the browser that set it. It is a
 * functional setting in the sense `CookieConsentBanner` documents — no consent
 * gate, no tracking, and covered by the privacy page's existing
 * "Anzeigeoptionen" clause.
 *
 * **Why the key is per tenant.** See {@link ../hooks/useTenantScopeId}: with
 * impersonation in the app, an unscoped key would show one farm's lens in
 * another's view. Without a tenant there is no key — this module then reads and
 * writes NOTHING rather than falling back to a shared one, because a shared
 * default is the very bug the scoping exists to prevent.
 *
 * **Why every access is wrapped.** A private window (or blocked/quota'd
 * storage) throws on both `getItem` and `setItem`. An unguarded write throws on
 * the way out of a click handler and takes the page down with it, so the write
 * is guarded as carefully as the read.
 *
 * Deliberately NOT validated here: whether the stored id names something this
 * tenant can actually be shown. Each page already owns that rule — the lens
 * registry on /farm, the count-derived lens list on /documents, #2419's visible
 * groups on Ernteprognose, #2420's data-bearing subtree on Preisspiegel — and
 * duplicating it here would give two places that can disagree about what a
 * valid lens is.
 */

/** The read/write pair for one page's preference, already bound to its prefix. */
export interface LensPreference {
  /** The storage key for a tenant, or null when there is no tenant to key by. */
  key(tenantId: string | null): string | null;
  /** The stored value for a tenant, or null (no tenant / none stored / storage unusable). */
  read(tenantId: string | null): string | null;
  /** Remember a value for a tenant. A no-op without a tenant, or when storage refuses. */
  write(tenantId: string | null, value: string): void;
}

/**
 * Bind the guarded per-tenant store to one key prefix. Call this once per page,
 * at module scope, and export the three functions under names that say what the
 * page stores — `createLensPreference('hofsync.farm.lense')`.
 */
export function createLensPreference(prefix: string): LensPreference {
  const key = (tenantId: string | null): string | null => {
    if (!tenantId) return null;
    return `${prefix}.${tenantId}`;
  };

  return {
    key,

    read(tenantId: string | null): string | null {
      const k = key(tenantId);
      if (!k || typeof window === 'undefined') return null;
      try {
        return window.localStorage.getItem(k) || null;
      } catch {
        /* swallow storage errors (private mode, blocked storage) */
        return null;
      }
    },

    write(tenantId: string | null, value: string): void {
      const k = key(tenantId);
      if (!k || typeof window === 'undefined') return;
      try {
        window.localStorage.setItem(k, value);
      } catch {
        /* swallow storage errors — the selection itself still lands */
      }
    },
  };
}
